import type { CSSProperties, ReactNode } from 'react';

import { IconChevronDownSmall } from '../../core/icons';

const BUBBLE_SHADOW =
    '-0.25px -0.25px 0px 0px rgba(0, 0, 0, 0.05), 0px 0px 1px 0px rgba(66, 66, 66, 0.11), 0px 0px 1px 0px rgba(66, 66, 66, 0.07), 0px 0px 1px 0px rgba(66, 66, 66, 0.02), inset -0.5px -0.5px 1px 0px rgba(0, 0, 0, 0.07), inset 1px 1px 1px 0px rgba(251, 251, 255, 0.75), inset 0px 0px 1px 0px rgba(66, 66, 66, 0)';
const BRIDGE_SHADOW_STYLE: CSSProperties = { boxShadow: BUBBLE_SHADOW };

export type BundleCardProps = {
    readonly lead: ReactNode;
    readonly bridge?: ReactNode;
    readonly cascade?: ReactNode;
    readonly className?: string;
};

export function BundleCard({ lead, bridge, cascade, className }: BundleCardProps) {
    return (
        <div className={`flex flex-col gap-4 font-sans ${className ?? ''}`}>
            {lead}
            {bridge}
            {cascade ? (
                <div className="animate-fade-in" style={{ animationDuration: '180ms', animationFillMode: 'both' }}>
                    {cascade}
                </div>
            ) : null}
        </div>
    );
}

export type BundleCardSectionProps = {
    readonly label: string;
    readonly status?: ReactNode;
    /** Header control rendered as a sibling of the toggle, so it can hold its own button without nesting buttons. Only shown while the section is expanded. */
    readonly action?: ReactNode;
    readonly expanded?: boolean;
    readonly onToggle?: () => void;
    readonly children?: ReactNode;
    readonly className?: string;
};

function BundleCardSection({
    label,
    status,
    action,
    expanded = true,
    onToggle,
    children,
    className,
}: BundleCardSectionProps) {
    const isClickable = typeof onToggle === 'function';
    const labelClassName =
        'min-w-0 flex-1 truncate text-left text-xs font-medium leading-tight text-black-600 [text-wrap:auto]';
    return (
        <div
            className={`relative w-full overflow-hidden rounded-xxl border border-gray-300/30 font-sans shadow-sm ${className ?? ''}`}
        >
            <div
                className={`flex min-h-9 items-center gap-0 bg-gray-200 py-1 pl-l pr-m transition-colors duration-200 ease ${isClickable ? 'hover:bg-gray-300/80' : ''}`}
            >
                {isClickable ? (
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={expanded}
                        className={`flex min-w-0 flex-1 items-center border-0 bg-transparent p-0 text-left ${labelClassName} font-sans`}
                    >
                        {label}
                    </button>
                ) : (
                    <span className={labelClassName}>{label}</span>
                )}
                <span className="ml-auto flex min-w-0 shrink-0 items-center gap-xs pl-s">
                    {status}
                    {expanded ? action : null}
                    {isClickable ? (
                        <button
                            type="button"
                            onClick={onToggle}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                            className="flex shrink-0 items-center border-0 bg-transparent p-0 font-sans"
                        >
                            <IconChevronDownSmall
                                size={14}
                                aria-hidden={true}
                                className={`shrink-0 text-black-400 transition-transform duration-200 ease ${expanded ? 'rotate-180' : ''}`}
                            />
                        </button>
                    ) : null}
                </span>
            </div>
            {expanded && children !== undefined ? <div className="bg-white-200 px-xl py-l">{children}</div> : null}
        </div>
    );
}

export type BundleCardBridgeProps = {
    readonly children: ReactNode;
    readonly className?: string;
};

function BundleCardBridge({ children, className }: BundleCardBridgeProps) {
    return (
        <div className="flex justify-start pb-0">
            <p
                className={`m-0 w-full max-w-[85%] rounded-xl border border-gray-200/80 bg-white-200/80 px-l py-s text-xs leading-[1.5] text-black-800 ${className ?? ''}`}
                style={BRIDGE_SHADOW_STYLE}
            >
                {children}
            </p>
        </div>
    );
}

export type BundleCardBridgeSkeletonProps = {
    readonly className?: string;
};

function BundleCardBridgeSkeleton({ className }: BundleCardBridgeSkeletonProps) {
    return (
        <div className="flex justify-start pb-0">
            <div
                className={`w-full max-w-[85%] rounded-xl border border-gray-200/80 bg-white-200/80 px-l py-s ${className ?? ''}`}
                style={BRIDGE_SHADOW_STYLE}
            >
                <div className="w-full max-w-md space-y-1.5">
                    <div className="h-2.5 w-[88%] animate-pulse rounded-sm bg-gray-200/80" />
                    <div className="h-2.5 w-[55%] animate-pulse rounded-sm bg-gray-200/70" />
                </div>
            </div>
        </div>
    );
}

BundleCard.Section = BundleCardSection;
BundleCard.Bridge = BundleCardBridge;
BundleCard.BridgeSkeleton = BundleCardBridgeSkeleton;
